import { useState, useRef, useEffect } from "react";
import { Plus, type LucideIcon } from "lucide-react";
import { cn } from "@tabipla/ui";

interface SpeedDialAction {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}

interface SpeedDialFabProps {
  actions: SpeedDialAction[];
  className?: string;
}

export function SpeedDialFab({ actions, className }: SpeedDialFabProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // If only one action, just execute it directly
  if (actions.length === 1) {
    const action = actions[0];
    return (
      <button
        onClick={action.onClick}
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg transition-all hover:bg-emerald-600 hover:shadow-xl active:scale-95",
          className
        )}
        aria-label={action.label}
      >
        <action.icon className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Action buttons */}
      <div
        className={cn(
          "absolute bottom-16 right-0 flex flex-col-reverse gap-3 transition-all duration-200",
          isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        {actions.map((action, index) => (
          <div
            key={index}
            className={cn(
              "flex items-center gap-3 transition-all duration-200",
              isOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            )}
            style={{ transitionDelay: isOpen ? `${index * 50}ms` : "0ms" }}
          >
            {/* Label */}
            <span className="whitespace-nowrap rounded-lg bg-gray-900 px-3 py-1.5 text-sm font-medium text-white shadow-lg">
              {action.label}
            </span>
            {/* Button */}
            <button
              onClick={() => {
                action.onClick();
                setIsOpen(false);
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl active:scale-95"
              aria-label={action.label}
            >
              <action.icon className="h-5 w-5" />
            </button>
          </div>
        ))}
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[-1] bg-black/20 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Main FAB button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-all hover:shadow-xl active:scale-95",
          isOpen
            ? "bg-gray-700 text-white hover:bg-gray-800"
            : "bg-emerald-500 text-white hover:bg-emerald-600"
        )}
        aria-label={isOpen ? "閉じる" : "メニューを開く"}
        aria-expanded={isOpen}
      >
        <Plus className={cn("h-6 w-6 transition-transform duration-200", isOpen && "rotate-45")} />
      </button>
    </div>
  );
}
